import axios from "axios";

import { api } from "@/api";
import { AptiError } from "@/core/errors";
import { getPhotoIDFileKey, getPhotoIDFileName } from "@/core/helpers/photoID";
import { validatePresignedResponse } from "@/core/helpers/s3";
import type { MimeType, GetPresignedUrlForPostResponse, UUID } from "@/core/types";
import { createS3FormData } from "@/lib/helpers/createS3FormData";

type UploadPhotoIDParams = {
  file: File;
  mimeType: MimeType;
  patientId: UUID;
};

export async function uploadPhotoID({ file, mimeType, patientId }: UploadPhotoIDParams) {
  let presignedResponse: GetPresignedUrlForPostResponse;

  // Step 1: Get presigned URL from backend
  try {
    presignedResponse = await api.sharedTyped.post_presigned_url({
      data: {
        content_type: mimeType,
        entity_id: patientId,
        entity_type: "patient_personal_id_image",
        file_key: getPhotoIDFileKey({ id: patientId }),
        file_name: getPhotoIDFileName({ id: patientId }),
        method: "post_object",
      },
    });

    // Handle null/undefined response from API
    if (!presignedResponse) {
      throw new AptiError({
        code: "S3_INVALID_PRESIGNED_RESPONSE",
        message: "Backend returned empty response - this may indicate a server error",
      });
    }

    // Validate response structure before proceeding
    if (!validatePresignedResponse(presignedResponse)) {
      throw new AptiError({
        code: "S3_INVALID_PRESIGNED_RESPONSE",
        message: "Backend returned invalid presigned URL response",
      });
    }
  } catch (error) {
    // Re-throw AptiError validation errors as-is
    if (error instanceof AptiError) {
      throw error;
    }

    // Wrap other errors with context
    throw new AptiError({
      cause: error,
      code: "S3_PRESIGNED_URL_FAILED",
      message: "Failed to get presigned URL for Personal ID",
    });
  }

  // Step 2: Upload file to S3
  try {
    const formData = createS3FormData({ fields: presignedResponse.fields, file });

    const uploadResponse = await axios.post(presignedResponse.url, formData, {
      timeout: 60000, // 60 seconds for large files
      validateStatus: (status) => status === 200 || status === 204,
    });

    return uploadResponse;
  } catch (error) {
    // Enhance S3 error with status code if available
    if (axios.isAxiosError(error)) {
      const statusInfo = error.response?.status ? `(Status: ${error.response.status})` : "";

      throw new AptiError({
        cause: error,
        code: "S3_UPLOAD_FAILED",
        context: {
          fileSize: file.size,
          fileType: file.type,
        },
        message: `Failed to upload Personal ID to S3 ${statusInfo}`,
      });
    }

    // Wrap unknown errors
    throw new AptiError({
      cause: error,
      code: "S3_UPLOAD_FAILED",
      context: {
        fileSize: file.size,
        fileType: file.type,
      },
      message: "Personal ID upload to S3 failed with unknown error",
    });
  }
}
